import { useState, useEffect } from 'react'
import { Oval as LoadingSpinner } from 'react-loader-spinner'
import './CreatePartnerModal.css'
import { CreatePartnerResponse, GetPartnerCreationStatusResponse, ProvisioningService } from '@/services/ProvisioningService'
import { Partner } from '@/services/PartnerService'

interface CreatePartnerModalProps {
  onClose: () => void
  onSuccess: () => void
  partners: Partner[]
}

export default function CreatePartnerModal({ onClose, onSuccess, partners }: CreatePartnerModalProps) {
  const [partnerName, setPartnerName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [creationRequest, setCreationRequest] = useState<CreatePartnerResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null)
  const [actionInProgress, setActionInProgress] = useState<'cancel' | 'retry' | 'delete' | null>(null)

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [pollingInterval])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!partnerName.trim()) {
      setError('Partner name is required')
      return
    }

    setIsSubmitting(true)
    setIsSubmitDisabled(true)

    try {
      // Initiate partner creation workflow
      const result = await ProvisioningService.create(partnerName.trim())
      setCreationRequest(result)

      // Start polling for status updates
      const interval = setInterval(async () => {
        try {
          const updatedRequest = await ProvisioningService.getStatus(result.requestId)
          setCreationRequest(updatedRequest)

          if (updatedRequest.status === 'Completed' || updatedRequest.status === 'Failed') {
            clearInterval(interval)
            setPollingInterval(null)
            if (updatedRequest.status === 'Completed') {
              onSuccess()
            }
          }
        } catch (err) {
          console.error('Error polling creation status:', err)
        }
      }, 3000)

      setPollingInterval(interval)
    } catch (err) {
      console.error('Error creating partner:', err)
      setError('Failed to create channel partner')
      setIsSubmitting(false)
      setIsSubmitDisabled(false)
    }
  }

  const getStepStatus = (step: string, currentStep: string, request: GetPartnerCreationStatusResponse) => {
    const steps = ['CreatingKeycloakGroup', 'ProvisioningDatabase', 'CreatingPartnerRecord', 'Completed']
    const currentIndex = steps.indexOf(currentStep)
    const stepIndex = steps.indexOf(step)

    if (request.status === 'Failed' && currentStep === step) {
      return 'failed'
    }

    if (stepIndex < currentIndex || currentStep === 'Completed') {
      return 'completed'
    }

    if (stepIndex === currentIndex) {
      return 'in-progress'
    }

    return 'pending'
  }

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✓'
      case 'in-progress':
        return '⟳'
      case 'failed':
        return '✗'
      default:
        return '○'
    }
  }

  const handlePartnerNameChange = (value: string) => {
    setPartnerName(value)
    if (partners.flatMap(partner => partner.name.toLocaleLowerCase()).includes(value.toLocaleLowerCase())) {
      setError('Partner name already exists. Please use a unique name.')
      setIsSubmitDisabled(true)
      return;
    }

    setError(null)
    setIsSubmitDisabled(false)
  }

  const handleCancel = async () => {
    if (!creationRequest || !creationRequest.canCancel) return

    if (!confirm('Are you sure you want to cancel this partner creation? This action cannot be undone.')) {
      return
    }

    setActionInProgress('cancel')
    setError(null)

    try {
      await ProvisioningService.cancelCreation(creationRequest.requestId)

      // Stop polling
      if (pollingInterval) {
        clearInterval(pollingInterval)
        setPollingInterval(null)
      }

      // Refresh status one more time to show cancelled state
      const updatedRequest = await ProvisioningService.getStatus(creationRequest.requestId)
      setCreationRequest(updatedRequest)
    } catch (err) {
      console.error('Error cancelling partner creation:', err)
      setError(err instanceof Error ? err.message : 'Failed to cancel partner creation')
    } finally {
      setActionInProgress(null)
    }
  }

  const handleRetry = async () => {
    if (!creationRequest || !creationRequest.canRetry) return

    setActionInProgress('retry')
    setError(null)

    try {
      await ProvisioningService.retryCreation(creationRequest.requestId)

      // Refresh status to show retry in progress
      const updatedRequest = await ProvisioningService.getStatus(creationRequest.requestId)
      setCreationRequest(updatedRequest)

      // Restart polling if not already running
      if (!pollingInterval) {
        const interval = setInterval(async () => {
          try {
            const statusUpdate = await ProvisioningService.getStatus(creationRequest.requestId)
            setCreationRequest(statusUpdate)

            if (statusUpdate.status === 'Completed' || statusUpdate.status === 'Failed') {
              clearInterval(interval)
              setPollingInterval(null)
              if (statusUpdate.status === 'Completed') {
                onSuccess()
              }
            }
          } catch (err) {
            console.error('Error polling creation status:', err)
          }
        }, 3000)

        setPollingInterval(interval)
      }
    } catch (err) {
      console.error('Error retrying partner creation:', err)
      setError(err instanceof Error ? err.message : 'Failed to retry partner creation')
    } finally {
      setActionInProgress(null)
    }
  }

  const handleDelete = async (deleteResources = false) => {
    if (!creationRequest) return

    const message = deleteResources
      ? 'Are you sure you want to delete this request and ALL associated resources (database, Keycloak group)? This action cannot be undone!'
      : 'Are you sure you want to delete this request? Created resources will remain but the request record will be removed.'

    if (!confirm(message)) {
      return
    }

    setActionInProgress('delete')
    setError(null)

    try {
      await ProvisioningService.deleteCreationRequest(creationRequest.requestId)

      // Stop polling
      if (pollingInterval) {
        clearInterval(pollingInterval)
        setPollingInterval(null)
      }

      // Close modal after successful deletion
      onClose()
    } catch (err) {
      console.error('Error deleting partner creation:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete partner creation request')
      setActionInProgress(null)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="create-partner-modal-content">
        <div className="modal-header">
          <h2>Create Channel Partner</h2>
          <p>This will automatically create a Keycloak group and provision a new database</p>
          {!creationRequest && (
            <button className="modal-close" onClick={onClose}>×</button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="create-partner-form">
          {!creationRequest && (
            <>
              <div className="form-field-group">
                <label htmlFor="partnerName">Partner Name *</label>
                <input
                  type="text"
                  id="partnerName"
                  value={partnerName}
                  onChange={(e) => handlePartnerNameChange(e.target.value)}
                  placeholder="Enter channel partner name"
                  disabled={isSubmitting}
                  required
                  maxLength={200}
                  autoFocus
                />
                <span className="field-help">
                  The system will automatically create a Keycloak group and assign a unique partner ID
                </span>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={isSubmitDisabled}>
                  {isSubmitting ? 'Creating...' : 'Create Partner'}
                </button>
              </div>
            </>
          )}

          {creationRequest && (
            <div className="creation-progress">
              <div className="progress-header">
                <h3>Creating: {creationRequest.partnerName}</h3>
                {creationRequest.partnerId && (
                  <span className="partner-id-badge">Partner ID: {creationRequest.partnerId}</span>
                )}
              </div>

              {/* Progress Bar */}
              <div className="progress-bar-container">
                <div className="progress-bar-wrapper">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${creationRequest.progress.percentComplete}%` }}
                  />
                </div>
                <div className="progress-info-row">
                  <span className="progress-percent">{creationRequest.progress.percentComplete}%</span>
                </div>
                <p className="progress-status-message">{creationRequest.progress.statusMessage}</p>
              </div>

              <div className="progress-steps">
                {creationRequest.progress.steps.map((step, index) => (
                  <div key={index} className={`progress-step ${step.status.toLowerCase()}`}>
                    <div className="step-icon">
                      {step.status === 'Completed' ? (
                        <span className="step-icon-text success">✓</span>
                      ) : step.status === 'InProgress' ? (
                        <LoadingSpinner
                          height={20}
                          width={20}
                          color="#7fbc04"
                          visible={true}
                          ariaLabel="step-loading"
                          secondaryColor="#e5e5e5"
                          strokeWidth={3}
                          strokeWidthSecondary={3}
                        />
                      ) : (
                        <span className="step-icon-text pending">○</span>
                      )}
                    </div>
                    <div className="step-content">
                      <div className="step-title">{index + 1}. {step.name}</div>
                      <div className="step-details">
                        {step.details && (
                          <span className="step-status">{step.details}</span>
                        )}
                        {step.completedAt && (
                          <span className="step-completed-time">
                            Completed: {new Date(step.completedAt).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {creationRequest.status === 'InProgress' && (
                <div className="status-info">
                  <LoadingSpinner
                    height={32}
                    width={32}
                    color="#7fbc04"
                    visible={true}
                    ariaLabel="creation-progress-spinner"
                    secondaryColor="#e5e5e5"
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                  />
                  <p className="status-message">
                    Creation in progress...
                  </p>
                  {creationRequest.canCancel && (
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={handleCancel}
                        disabled={actionInProgress !== null}
                      >
                        {actionInProgress === 'cancel' ? (
                          <>
                            <LoadingSpinner
                              height={16}
                              width={16}
                              color="#ffffff"
                              visible={true}
                              ariaLabel="cancel-spinner"
                              secondaryColor="rgba(255,255,255,0.3)"
                              strokeWidth={3}
                              strokeWidthSecondary={3}
                            />
                            <span>Cancelling...</span>
                          </>
                        ) : (
                          'Cancel Creation'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {creationRequest.status === 'Completed' && (
                <div className="status-success">
                  <span className="success-icon">✓</span>
                  <p className="success-message">Channel partner created successfully!</p>
                  <div className="success-details">
                    <div className="success-detail">
                      <span>Partner ID:</span>
                      <strong>{creationRequest.partnerId}</strong>
                    </div>
                    <div className="success-detail">
                      <span>Keycloak Group:</span>
                      <strong>{creationRequest.keycloakGroupId}</strong>
                    </div>
                    <div className="success-detail">
                      <span>Database:</span>
                      <strong>{creationRequest.databaseName}</strong>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn-submit" onClick={onClose}>
                      Close
                    </button>
                  </div>
                </div>
              )}

              {creationRequest.status === 'Failed' && (
                <div className="status-error">
                  <span className="error-icon">✗</span>
                  <p className="error-message-title">Creation Failed</p>
                  {creationRequest.errorMessage && (
                    <p className="error-message-text">{creationRequest.errorMessage}</p>
                  )}
                  {error && (
                    <p className="error-message-text">{error}</p>
                  )}
                  <div className="form-actions">
                    {creationRequest.canRetry && (
                      <button
                        type="button"
                        className="btn-submit"
                        onClick={handleRetry}
                        disabled={actionInProgress !== null}
                      >
                        {actionInProgress === 'retry' ? (
                          <>
                            <LoadingSpinner
                              height={16}
                              width={16}
                              color="#ffffff"
                              visible={true}
                              ariaLabel="retry-spinner"
                              secondaryColor="rgba(255,255,255,0.3)"
                              strokeWidth={3}
                              strokeWidthSecondary={3}
                            />
                            <span>Retrying...</span>
                          </>
                        ) : (
                          'Retry Creation'
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleDelete(false)}
                      disabled={actionInProgress !== null}
                    >
                      {actionInProgress === 'delete' ? (
                        <>
                          <LoadingSpinner
                            height={16}
                            width={16}
                            color="#ffffff"
                            visible={true}
                            ariaLabel="delete-spinner"
                            secondaryColor="rgba(255,255,255,0.3)"
                            strokeWidth={3}
                            strokeWidthSecondary={3}
                          />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        'Delete Request'
                      )}
                    </button>
                    <button type="button" className="btn-cancel" onClick={onClose}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
